"use client";

import { useEffect, useState } from "react";

import { VIOLET_BRIGHT } from "@/components/ui/organisms/chord-practice/palette";

/**
 * La page du sas : le décor, la titraille et les gestes, sans le propos.
 *
 * Le sas est le dernier écran immobile avant le décompte : il nomme le niveau
 * qu'on ouvre et ce qu'on va y jouer. Le décor, la titraille, le filet et les
 * colonnes vivent ici plutôt que dans l'écran, pour que sa cérémonie ne dépende
 * pas de ce qu'il raconte.
 *
 * Ils ont servi deux écrans un moment : celui-ci, et une entrée du programme
 * qui présentait les deux parcours. Cette entrée doublait la fiche du morceau,
 * qui pose déjà le titre, le compositeur et « Apprendre » ; le choix des
 * parcours y est retourné, dans la langue de la fiche et non dans celle du sas.
 * Voir `PieceOverviewOverlay`.
 *
 * Rien dans ce fichier ne connaît l'harmonie, le morceau ni le niveau : c'est
 * la condition pour que la cérémonie survive au prochain changement de l'écran
 * qui s'en sert.
 */

/**
 * La largeur au-delà de laquelle une colonne cesse d'être lisible.
 *
 * L'écran occupe toute la pièce, mais une ligne de texte, non : à un bras de
 * distance, passé une soixantaine de signes l'oeil perd le début de la ligne
 * suivante. Les colonnes s'écartent donc au lieu de s'élargir, et ce qui
 * remplit l'espace est leur écartement.
 */
export const COLUMN_MAX_PX = 560;

/**
 * `@max-[936px]` : la largeur de boîte sous laquelle la page tient sur une
 * seule colonne.
 *
 * Déduite et non choisie : deux pistes tiennent à partir de `2 × 400 + 48` de
 * grille, et la grille est la boîte moins ses 88 pixels de rembourrage. La
 * colonne de droite s'en sert pour cesser d'être poussée au bord.
 *
 * Écrit en toutes lettres plutôt que tenu dans une constante : Tailwind lit le
 * **texte** des fichiers, donc une classe composée par interpolation n'est
 * jamais générée, et la règle manquerait sans que rien ne le signale.
 *
 * En variante de conteneur et non en `@media` : cet écran vit dans le cadre que
 * le studio lui laisse, et un palier en pixels de fenêtre plierait au mauvais
 * moment dès qu'on ouvre le panneau de gauche.
 */

/**
 * Sous cette largeur, une colonne cesse d'en être une.
 *
 * 400 px tiennent la ligne du tableau d'accords en entier — chiffrage, degré,
 * les trois notes, la fréquence et son rail — et une ligne de prose de
 * cinquante signes. En dessous, `auto-fit` rend la main à une seule colonne.
 *
 * C'était 280, ce qui tenait la liste d'avant : trois cellules courtes et rien
 * d'autre. Le tableau en demande plus, et 280 lui faisaient replier ses notes
 * sur deux lignes plutôt que replier la page. C'est aussi ce qui met la
 * tablette en portrait sur une seule colonne, où deux colonnes de trois cent
 * soixante-dix pixels ne sont ni un tableau ni une mesure de lecture.
 */
const COLUMN_MIN_PX = 400;

/**
 * Le décor et la titraille, communs aux deux temps du sas.
 *
 * Pas une carte, la pièce entière. Il y en a eu une, avec son liseré et son
 * ombre portée, et elle avait le défaut que toute plaque a dans une boîte à
 * peine plus grande qu'elle : elle prenait presque toute la place sans la
 * prendre, et la marge qui restait autour ne se lisait pas comme une
 * respiration mais comme un oubli. L'écran occupe donc son espace en entier, de
 * bord à bord, et la cérémonie tient dans ce qu'il dit et non dans un cadre qui
 * la contient.
 *
 * Ce que le studio, lui, borne toujours : le bas, au-dessus du vrai clavier,
 * qui ne disparaît dans aucun écran du parcours.
 */
export function SasPage({
  label,
  overline,
  aside,
  title,
  lead = null,
  figure = null,
  children,
}: {
  /** Ce que l'écran s'appelle pour les technologies d'assistance. */
  readonly label: string;
  readonly overline: React.ReactNode;
  /**
   * Le bout droit du filet : où l'on est, et ce qu'il y a à y faire.
   *
   * Deux étiquettes sur un même filet ne peuvent pas porter la même encre, et
   * c'est celle-ci qui prend le violet : le surtitre nomme le morceau, qu'on a
   * choisi et qu'on connaît, tandis que celle-ci dit à quel barreau on entre,
   * ce qu'aucun autre objet de l'écran ne dit.
   */
  readonly aside: React.ReactNode;
  readonly title: string;
  /**
   * L'amorce, sous le titre et dans la même colonne que lui.
   *
   * Elle appartient à la titraille et non au corps : c'est la phrase qui donne
   * au niveau sa taille dans ce morceau-ci, et elle se lit avant le filet qui
   * ouvre le travail.
   */
  readonly lead?: React.ReactNode;
  /**
   * La fiche posée à droite de la titraille, ou `null` quand l'écran n'en a
   * pas.
   *
   * Elle tombe sous le titre quand la place manque, et pas dans une colonne à
   * elle : la titraille et sa fiche sont un seul bloc, qui se plie.
   */
  readonly figure?: React.ReactNode;
  readonly children: React.ReactNode;
}) {
  /*
    L'entrée. Une image d'attente puis la transition, ce qui est le seul moyen
    d'avoir un état de départ peint : poser la classe finale au premier rendu
    ne transitionne rien.

    `motion-reduce` la désarme entièrement. Elle ne porte aucune information,
    donc elle n'a rien à laisser derrière elle quand on la refuse.
  */
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      className="@container relative flex h-full flex-col"
      // Toute la peinture de l'écran est ici, en couches de fond de la boîte
      // qui défile, et non en calques posés dedans.
      //
      // **Le fond opaque appartient à la page** et non à qui la monte. L'aurore
      // et la vignette sont des voiles : posées seules, elles laissent passer ce
      // qu'il y a derrière, et l'entrée du programme montée dans le studio se
      // lisait par-dessus les notes du piano roll. La scène harmonique le
      // peignait pour son propre sas, le studio croyait que la page le peignait,
      // et personne ne le peignait.
      //
      // **Et des fonds plutôt que des calques**, parce qu'un `inset-0` dans une
      // boîte qui défile mesure la fenêtre et non le contenu : les deux voiles
      // s'arrêtaient net à la hauteur du premier écran et le bas de la page,
      // une fois déroulée, tombait sur l'aplat nu. Un fond, lui, se peint sur la
      // boîte et ne défile pas avec ce qu'elle contient, si bien que la vignette
      // referme toujours les bords qu'on regarde.
      //
      // L'ordre est celui de l'empilement, du dessus vers le dessous : la
      // vignette ferme la composition, l'aurore l'éclaire, l'aplat la porte.
      style={{
        overflowY: "auto",
        // Un conteneur de **taille** et non de largeur seule.
        //
        // Le sas tient dans le cadre que le studio lui laisse — la fenêtre moins
        // son en-tête et moins le vrai clavier — et ce cadre est cent à deux
        // cents pixels plus court que la fenêtre. Un corps de texte réglé sur la
        // hauteur de la fenêtre débordait donc de tout ce que le studio prend,
        // et le geste tombait sous la ligne de flottaison : on arrivait sur un
        // écran qui a l'air d'un cul-de-sac.
        //
        // `cqh` mesure le cadre lui-même, ce qui rend la question calculable au
        // lieu d'être devinée par paliers de fenêtre. La boîte défile toujours,
        // mais comme filet de sécurité et non comme mode de lecture.
        containerType: "size",
        background: `
          radial-gradient(ellipse 82% 74% at 50% 46%, transparent 0%, rgba(0,0,0,0.42) 100%),
          radial-gradient(ellipse 800px 400px at 50% 70%, rgba(139,92,246,0.15) 0%, transparent 60%),
          radial-gradient(ellipse 600px 300px at 30% 30%, rgba(103,232,249,0.06) 0%, transparent 70%),
          var(--color-bg-deep)
        `,
      }}
      role="dialog"
      aria-label={label}
    >
      <div
        // Le bas est aussi épais que les côtés, et c'est le seul des quatre qui
        // ait une raison de l'être : le vrai clavier du studio commence
        // exactement là où cette boîte s'arrête, sans bordure ni ombre entre les
        // deux. Trente pixels ne se lisaient pas comme une marge mais comme une
        // coupure, surtout quand le dernier objet de la page est lui-même un
        // clavier.
        //
        // Il se resserre quand le cadre est court, et les côtés non : sur un
        // cadre de six cent cinquante pixels, quarante-quatre en haut et
        // quarante-quatre en bas sont les seuls à décider si le geste tombe
        // au-dessus ou au-dessous de la ligne de flottaison. Un rembourrage
        // qu'on ne voit pas ne vaut pas un bouton qu'on ne voit pas.
        // `min-h-full shrink-0` et non `h-full` : à hauteur imposée, la boîte
        // fait exactement celle du cadre et son contenu la déborde par le bas,
        // donc le rembourrage de 44 pixels reste en haut de l'écran pendant que
        // la dernière ligne va se coller au clavier. En hauteur minimale elle
        // remplit le cadre quand il y a de la place — ce dont `grow` et les
        // marges automatiques des colonnes ont besoin — et grandit avec son
        // contenu sinon, en emportant sa marge basse avec elle.
        //
        // `shrink-0` parce que la minimale ne suffit pas : élément d'une
        // colonne flex, la boîte se laisse comprimer à la hauteur du cadre et
        // le débordement revient tel quel.
        className={`relative flex min-h-full shrink-0 flex-col px-[44px] @max-[820px]:px-[22px] pt-[clamp(12px,4.6cqh,34px)] pb-[clamp(16px,4.2cqh,44px)] transition-all duration-300 ease-out motion-reduce:transition-none ${
          visible ? "translate-y-0 opacity-100" : "translate-y-[6px] opacity-0"
        }`}
      >
        <Overline aside={aside}>{overline}</Overline>

        {/*
          La titraille et sa fiche, sur une seule ligne qui se plie.

          Alignées par le **bas** : la fiche est plus haute que le titre, et
          deux blocs alignés par le haut auraient laissé le nom du niveau
          flotter au-dessus d'un vide. Par le bas, la dernière ligne de l'amorce
          et le dernier objet de la fiche tombent sur le même trait, qui est
          déjà celui du filet juste dessous.
        */}
        <div
          className="flex flex-wrap items-end"
          // La marge est portée par la ligne et non par le titre : la fiche est
          // le plus haut des deux blocs, donc c'est elle qui touchait le filet
          // pendant que le titre gardait la sienne.
          style={{ marginTop: "clamp(8px, 1.8cqh, 14px)", gap: "14px clamp(24px, 4cqw, 64px)" }}
        >
          <div style={{ flex: "1 1 360px", minWidth: 0 }}>
            {/*
              Le titre, dans la titraille de la page du morceau : Inter en
              graisse 200, très grand, l'interlettrage resserré, et un
              remplissage en dégradé vertical du blanc presque plein vers le
              gris. C'est ce dégradé qui fait la lumière, pas la couleur : la
              lettre s'éteint vers le bas comme si elle était éclairée d'en haut.

              Le rembourrage sous la ligne de base réserve la place des
              jambages : une interligne serrée avec `background-clip: text` les
              tronque sur la plupart des navigateurs sans lui.
            */}
            <h2
              className="m-0 text-[clamp(28px,7cqh,60px)] leading-[1.0]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 200,
                letterSpacing: "-0.035em",
                paddingBottom: "0.15em",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.58) 95%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {title}
            </h2>

            {lead}
          </div>

          {figure !== null && (
            // La fiche prend **la mesure d'une colonne**, et c'est le seul
            // réglage qui tienne des deux côtés du pli.
            //
            // Dépliée, elle surplombe la colonne de droite et se cale sur son
            // bord : une fiche plus étroite laissait un décrochement de quatre-
            // vingt-dix pixels entre son bord gauche et le texte juste dessous.
            // Repliée, tout descend sur une seule colonne, et une fiche qu'on
            // aurait laissée grandir aurait été le seul objet de la page plus
            // large que les autres.
            //
            // Les deux bases se disputent la ligne : passé le pli leur somme ne
            // tient plus et la fiche passe dessous, à la même mesure.
            <div className="min-w-0" style={{ flex: "1 1 400px", maxWidth: COLUMN_MAX_PX }}>
              {figure}
            </div>
          )}
        </div>

        {children}
      </div>
    </div>
  );
}

/** Le surtitre : le cadre dans lequel le titre se lit, et où l'on en est. */
function Overline({
  children,
  aside,
}: {
  readonly children: React.ReactNode;
  readonly aside: React.ReactNode;
}) {
  return (
    <div className="flex items-center" style={{ gap: 12, height: 14 }}>
      {/*
        Le filet, repris de la carte d'accueil. C'est la marque de cérémonie de
        la maison : deux traits courts qui encadrent une ligne monospace, et
        c'est à peu près tout ce qui sépare un surtitre d'une étiquette.
      */}
      <span
        aria-hidden
        className="block shrink-0"
        style={{ width: 22, height: 1, background: VIOLET_BRIGHT, opacity: 0.5 }}
      />

      <div
        className="font-mono uppercase truncate"
        style={{
          fontSize: "clamp(9.5px, 1.5cqh, 11.5px)",
          lineHeight: "14px",
          letterSpacing: "0.24em",
          color: "rgba(255,255,255,0.78)",
        }}
      >
        {children}
      </div>

      <span
        aria-hidden
        className="block min-w-[22px] grow"
        style={{ height: 1, background: VIOLET_BRIGHT, opacity: 0.2 }}
      />

      <div
        className="font-mono uppercase shrink-0"
        style={{
          fontSize: "clamp(9.5px, 1.5cqh, 11.5px)",
          lineHeight: "14px",
          letterSpacing: "0.24em",
          color: VIOLET_BRIGHT,
        }}
      >
        {aside}
      </div>
    </div>
  );
}

/**
 * L'amorce, en deux phrases dont la seconde peut ne pas exister.
 *
 * Séparées par un retour à la ligne et non par une espace : ce sont deux faits
 * de nature différente, et les coller en ferait un paragraphe qu'on survole.
 */
export function SasLead({
  first,
  second,
}: {
  readonly first: string;
  readonly second: string | null;
}) {
  return (
    <p
      className="text-[clamp(15px,2.6cqh,22px)]"
      style={{
        margin: "12px 0 0",
        fontFamily: "Inter, sans-serif",
        fontWeight: 400,
        lineHeight: 1.4,
        letterSpacing: "0.005em",
        color: "rgba(255,255,255,0.78)",
      }}
    >
      {first}
      {second !== null && (
        <>
          <br />
          {second}
        </>
      )}
    </p>
  );
}

/**
 * Le filet qui sépare la tête de cérémonie du corps de travail.
 *
 * Sans lui, le titre en graisse fine et le corps du texte appartiennent au même
 * bloc et la titraille perd ce qu'elle vient de gagner.
 */
export function SasRule() {
  return (
    <div
      aria-hidden
      style={{
        height: 1,
        margin: "clamp(9px, 2.1cqh, 16px) 0 0",
        background:
          "linear-gradient(90deg, rgba(196,181,253,0.3) 0%, rgba(255,255,255,0.08) 45%, transparent 100%)",
      }}
    />
  );
}

/**
 * Les deux colonnes, aux deux bords de la pièce et l'air au milieu.
 *
 * Elles prennent la hauteur qui reste, et le geste de chacune descend au pied
 * de la sienne : les deux colonnes n'ont jamais la même longueur, donc une
 * colonne courte alignée en haut laisserait un trou sous son bouton ; posés en
 * bas, ils tiennent les angles et la composition se tend d'un coin à l'autre.
 *
 * Le pliage se lit sur la **boîte** et non sur la fenêtre : cet écran vit dans
 * le cadre que le studio lui laisse, entre la barre latérale et la minimap, et
 * un point de rupture en pixels de viewport aurait plié au mauvais moment dès
 * qu'on ouvre ou ferme le panneau de gauche. `auto-fit` sur une largeur
 * minimale fait le même travail sans nommer de palier.
 */
export function SasColumns({
  left,
  right,
}: {
  /** La colonne de gauche, ou `null` quand cet écran n'en a pas. */
  readonly left: React.ReactNode;
  readonly right: React.ReactNode;
}) {
  return (
    <div
      className="min-h-0 grow"
      style={{
        display: "grid",
        // Des fractions et non des pistes plafonnées : une piste bornée à sa
        // mesure de lecture plus la gouttière dépasse vite la largeur
        // disponible, et `auto-fit` replie alors tout sur une colonne au moment
        // où il y avait le plus de place. La mesure se borne donc à l'intérieur
        // de la colonne, pas sur la piste.
        gridTemplateColumns:
          left === null
            ? "1fr"
            : `repeat(auto-fit, minmax(min(100%, ${COLUMN_MIN_PX}px), 1fr))`,
        gap: "14px 48px",
        marginTop: "clamp(9px, 2.1cqh, 16px)",
        alignItems: "stretch",
      }}
    >
      {left !== null && (
        <section className="flex flex-col" style={{ maxWidth: COLUMN_MAX_PX }}>
          {left}
        </section>
      )}

      <section
        // Poussée au bord droit de sa piste, pour que les deux colonnes
        // tiennent les deux bords de la pièce et que l'air soit au milieu.
        //
        // Et rendue au bord gauche une fois la page repliée. La piste fait alors
        // la largeur du bloc, mais la colonne garde sa mesure de lecture : une
        // colonne de 560 poussée à droite d'une piste de 750 laissait deux cents
        // pixels de vide à gauche, sous un tableau qui, lui, part du bord.
        className="flex flex-col justify-self-end @max-[936px]:justify-self-start"
        style={{ maxWidth: COLUMN_MAX_PX, width: "100%" }}
      >
        {right}
      </section>
    </div>
  );
}

/** Une phrase du sas, à l'encre et à la mesure de toutes les autres. */
export function Prose({
  children,
  top = 0,
}: {
  readonly children: React.ReactNode;
  readonly top?: number;
}) {
  return (
    <p
      className="text-[clamp(12.5px,2.05cqh,16px)]"
      style={{
        margin: `${top}px 0 0`,
        lineHeight: 1.45,
        color: "rgba(255,255,255,0.78)",
      }}
    >
      {children}
    </p>
  );
}

/**
 * Le geste d'une colonne : un mot et sa flèche, puis sa légende.
 *
 * Un mot coloré et non une pastille pleine, comme l'appel de la page du
 * morceau : dans une page de présentation, une pastille pleine se lit comme un
 * objet de formulaire posé sur un texte. La légende dessous dit ce qui arrive
 * quand on appuie, et c'est elle qui rend le choix honnête : les deux gestes de
 * l'entrée ne mènent pas au même endroit, et aucun des deux ne doit surprendre.
 *
 * ## Un seul ton, et c'est la couleur qui fait le geste
 *
 * Il y en a eu deux un moment : le violet pour l'offre, et un second, gris,
 * pour ce qui était présenté comme une sortie. Le gris avait l'encre et le
 * corps du texte qui l'entoure, et ne se distinguait que **par le survol** —
 * c'est-à-dire pas du tout sur une tablette posée sur un pupitre, qui est la
 * posture de référence du produit.
 *
 * Le remède essayé fut un contour, et c'était une seconde erreur : le produit
 * ne dessine nulle part de bouton à contour, et le seul objet qui y ressemble
 * est la pastille de renversement, qui est un **badge**. Une action qui prend
 * la forme d'un badge ment sur ce qu'elle est.
 *
 * Ce qui manquait n'était pas une forme, c'était la couleur. Le violet se donne
 * pour cliquable par lui seul : dans une page où rien d'autre ne l'est, c'est
 * le geste. Deux gestes de même rang le portent donc tous les deux, et se
 * ressemblent exactement — sans quoi la forme répondrait à la place du joueur.
 *
 * La flèche avance de deux pixels au survol : le signe qui dit « ça mène
 * quelque part », sans rien ajouter à l'écran.
 */
export function SasAction({
  label,
  hint,
  ariaLabel,
  disabled = false,
  onClick,
}: {
  readonly label: string;
  /** Ce qui arrive quand on appuie, ou `null` quand il n'y a rien à annoncer. */
  readonly hint: string | null;
  /**
   * Le nom entier du geste, quand le mot visible ne suffit pas à le dire.
   *
   * Deux colonnes qui offrent chacune « Commencer » se distinguent à l'œil par
   * leur titre, qui est juste au-dessus ; une liste de gestes lue à voix haute,
   * elle, n'a pas cette colonne. Le mot reste court parce qu'on le balaie, et
   * le nom complet est dit à qui écoute.
   */
  readonly ariaLabel?: string;
  /** Quelque chose est déjà en train de se poser : on n'en demande pas deux. */
  readonly disabled?: boolean;
  readonly onClick: () => void;
}) {
  const nudge = (target: HTMLElement, by: string) => {
    const arrow = target.querySelector<HTMLSpanElement>("[data-arrow]");

    if (arrow !== null) {
      arrow.style.transform = `translateX(${by})`;
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        disabled={disabled}
        className="inline-flex items-center text-left transition-colors duration-200 hover:brightness-125 focus-visible:brightness-125 disabled:cursor-default disabled:opacity-50 motion-reduce:transition-none"
        style={{
          gap: 10,
          // Quarante-quatre pixels de haut, et le rembourrage vertical seul :
          // c'est la cible que demande un doigt sur un pupitre, et la cible ne
          // doit pas décaler le mot du bord de sa colonne, où il tient l'angle
          // de la composition.
          //
          // Elle se resserre à trente-quatre quand le cadre descend sous six
          // cents pixels, ce qui est le dernier arbitrage possible et pas un
          // bon : une cible plus petite se vise moins bien. Mais un bouton
          // qu'on ne voit pas ne se vise pas du tout, et c'est l'alternative.
          minHeight: "clamp(34px, 6.5cqh, 44px)",
          padding: "clamp(3px, 0.9cqh, 6px) 0",
          border: "none",
          background: "transparent",
          color: VIOLET_BRIGHT,
          fontFamily: "Inter, sans-serif",
          fontSize: "clamp(18px, 3.1cqh, 26px)",
          fontWeight: 600,
          letterSpacing: "-0.005em",
          cursor: "pointer",
        }}
        onMouseEnter={(event) => nudge(event.currentTarget, "2px")}
        onMouseLeave={(event) => nudge(event.currentTarget, "0")}
        onFocus={(event) => nudge(event.currentTarget, "2px")}
        onBlur={(event) => nudge(event.currentTarget, "0")}
      >
        {label}
        <span
          aria-hidden
          data-arrow
          className="inline-block transition-transform duration-200 motion-reduce:transition-none"
        >
          →
        </span>
      </button>

      {hint !== null && (
        <div
          style={{
            marginTop: 4,
            fontSize: "clamp(11.5px, 1.7cqh, 13.5px)",
            color: "var(--color-text-soft)",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
